import React, { useMemo, useState } from 'react';
import {
    PolarAngleAxis,
    PolarGrid,
    PolarRadiusAxis,
    Radar,
    RadarChart,
    ResponsiveContainer,
    Tooltip,
} from 'recharts';
import { ChevronDown, ChevronUp, Sparkles, Target } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const textToSentences = (text) => {
    if (!text || typeof text !== 'string') return [];
    return text
        .replace(/\*\*/g, '')
        .split(/[.\n]/)
        .map((line) => line.trim())
        .filter(Boolean);
};

const pickSummarySentence = (...texts) => {
    for (const text of texts) {
        const first = textToSentences(text)[0];
        if (first) return first;
    }
    return '';
};

const buildHashtags = (data) => {
    const tags = [];

    if (data?.base_type) tags.push(`#${data.base_type.replace(/\s+/g, '')}`);
    if (data?.spice_type) tags.push(`#${data.spice_type.replace(/\s+/g, '')}`);
    if (data?.approachability?.type) {
        tags.push(`#${String(data.approachability.type).replace(/\s+/g, '')}`);
    }

    return tags.slice(0, 3);
};

const buildStrengthSummary = (scores) => {
    return Object.entries(scores || {})
        .filter(([, value]) => Number.isFinite(Number(value)))
        .sort((a, b) => Number(b[1]) - Number(a[1]))
        .slice(0, 3)
        .map(([key, value]) => `${key} ${Math.round(Number(value))}`);
};

const FACE_METRIC_LABELS = {
    strong: 'Strong',
    deep: 'Deep',
    sexy: 'Sexy',
    noble: 'Noble',
    cute: 'Cute',
    pure: 'Pure',
    hip: 'Hip',
    soft: 'Soft',
    cool: 'Cool',
    classic: 'Classic',
};

const formatMetricLabel = (key) => {
    const normalized = String(key || '').trim();
    if (!normalized) return 'Metric';
    return FACE_METRIC_LABELS[normalized.toLowerCase()]
        || normalized.replace(/[_-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const getBabySchemaSummary = (score, t) => {
    if (score >= 70) return t('face_result.detail.persona.baby_high');
    if (score >= 45) return t('face_result.detail.persona.baby_mid');
    return t('face_result.detail.persona.baby_low');
};

const getMaturitySummary = (score, t) => {
    if (score >= 70) return t('face_result.detail.persona.maturity_high');
    if (score >= 45) return t('face_result.detail.persona.maturity_mid');
    return t('face_result.detail.persona.maturity_low');
};

const ExpandableBlock = ({ title, children, defaultOpen = false }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(defaultOpen);

    return (
        <div style={expandableStyle}>
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                style={expandableButtonStyle}
            >
                <span>{title}</span>
                <span style={expandableLabelStyle}>
                    {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    {open ? t('face_result.detail.common.collapse') : t('face_result.detail.common.view')}
                </span>
            </button>
            {open ? <div style={{ marginTop: '0.85rem' }}>{children}</div> : null}
        </div>
    );
};

const ScoreRadarChart = ({ baseScores, spiceScores, summaryLine, compact = false }) => {
    const { t } = useTranslation();
    const [showValues, setShowValues] = useState(false);

    const data = [
        ...Object.entries(baseScores || {}).map(([key, value]) => ({
            subject: key,
            label: formatMetricLabel(key),
            score: Number(value) || 0,
        })),
        ...Object.entries(spiceScores || {}).map(([key, value]) => ({
            subject: key,
            label: formatMetricLabel(key),
            score: Number(value) || 0,
        })),
    ];
    const primaryMetric = [...data].sort((a, b) => b.score - a.score)[0];

    return (
        <div
            onClick={() => setShowValues((prev) => !prev)}
            style={radarCardStyle}
        >
            <div style={radarHeaderStyle}>
                <div>
                    <div style={eyebrowStyle}>{t('face_result.detail.persona.attraction_distribution')}</div>
                    {summaryLine ? (
                        <div style={summaryCaptionStyle}>{summaryLine}</div>
                    ) : null}
                </div>
                <span style={{ fontSize: '0.75rem', color: showValues ? '#60a5fa' : '#64748b' }}>
                    {showValues ? t('face_result.detail.persona.hide_values') : t('face_result.detail.persona.show_values')}
                </span>
            </div>

            {primaryMetric ? (
                <div style={compact ? compactPrimaryMetricCardStyle : primaryMetricCardStyle}>
                    <div style={primaryMetricCopyStyle}>
                        <span style={primaryMetricBadgeStyle}>{t('face_result.detail.persona.strongest_metric')}</span>
                        <strong style={primaryMetricTitleStyle}>{primaryMetric.label}</strong>
                    </div>
                    <div style={primaryMetricScoreStyle}>{Math.round(primaryMetric.score)}</div>
                </div>
            ) : null}

            <div style={compact ? compactMetricGridStyle : metricGridStyle}>
                {data.map((item) => {
                    const isPrimary = item.subject === primaryMetric?.subject;
                    return (
                        <div
                            key={item.subject}
                            style={{
                                ...(compact ? compactMetricItemStyle : metricItemStyle),
                                ...(isPrimary ? primaryMetricItemStyle : null),
                            }}
                        >
                            <div style={metricItemTopStyle}>
                                <span style={metricNameStyle}>{item.label}</span>
                                <strong style={isPrimary ? primaryMetricValueStyle : metricValueStyle}>
                                    {Math.round(item.score)}
                                </strong>
                            </div>
                            <div style={metricTrackStyle}>
                                <div
                                    style={{
                                        ...metricFillStyle,
                                        width: `${Math.max(0, Math.min(100, Math.round(item.score)))}%`,
                                        background: isPrimary ? 'linear-gradient(90deg, #f472b6, #60a5fa)' : '#64748b',
                                    }}
                                />
                            </div>
                            {isPrimary ? <span style={metricPrimaryLabelStyle}>PRIMARY</span> : null}
                        </div>
                    );
                })}
            </div>

            <ResponsiveContainer width="100%" height={compact ? 150 : 220}>
                <RadarChart data={data} margin={{ top: 0, right: 20, bottom: 0, left: 20 }}>
                    <PolarGrid stroke="rgba(255,255,255,0.1)" />
                    <PolarAngleAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    {showValues ? (
                        <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#64748b', fontSize: 9 }} />
                    ) : null}
                    <Radar
                        name="score"
                        dataKey="score"
                        stroke="#a78bfa"
                        fill="#a78bfa"
                        fillOpacity={0.3}
                        strokeWidth={2}
                    />
                    {showValues ? (
                        <Tooltip
                            contentStyle={{
                                background: '#1e293b',
                                border: '1px solid #334155',
                                borderRadius: '8px',
                                fontSize: '0.85rem',
                            }}
                            itemStyle={{ color: '#e2e8f0' }}
                        />
                    ) : null}
                </RadarChart>
            </ResponsiveContainer>
        </div>
    );
};

const PersonaHero = ({ data, isPaid, feedMode = false }) => {
    const { t } = useTranslation();
    const [showDescription, setShowDescription] = useState(false);

    if (!data) return null;

    const hashtags = useMemo(() => buildHashtags(data), [data]);
    const strengthSummary = useMemo(
        () => buildStrengthSummary({ ...(data?.scores?.base || {}), ...(data?.scores?.spice || {}) }),
        [data]
    );

    const oneLineSummary = pickSummarySentence(
        data?.analysis_report?.first_impression,
        data?.analysis_report?.gap_comment,
        data?.analysis_report?.base_comment,
        data?.description
    ) || t('face_result.detail.persona.summary_fallback');

    const firstImpressionItems = [
        {
            title: t('face_result.detail.impression.combined'),
            body: data?.analysis_report?.first_impression || pickSummarySentence(data?.description) || t('face_result.detail.impression.comfortable_fallback'),
        },
        {
            title: t('face_result.detail.impression.evidence'),
            body: data?.analysis_report?.evidence_summary || pickSummarySentence(data?.analysis_report?.spice_comment) || t('face_result.detail.impression.memorable_fallback'),
        },
        {
            title: t('face_result.detail.impression.approachability'),
            body: data?.approachability?.desc || t('face_result.detail.impression.reserved_fallback'),
        },
    ];

    const babySchemaScore = Math.round(Number(data?.biological_impression?.baby_schema) || 0);
    const hormoneSignalScore = Math.round(Number(data?.biological_impression?.hormone_signal) || 0);

    const bioCards = [
        {
            title: t('face_result.detail.persona.harmlessness'),
            subtitle: 'Baby Schema',
            value: babySchemaScore,
            summary: getBabySchemaSummary(babySchemaScore, t),
            color: '#f472b6',
        },
        {
            title: t('face_result.detail.persona.maturity_signal'),
            subtitle: 'Hormone Signal',
            value: hormoneSignalScore,
            summary: getMaturitySummary(hormoneSignalScore, t),
            color: '#a78bfa',
        },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={heroCardStyle}>
                <div style={heroGlowStyle} />

                <div style={heroBadgeStyle}>
                    <Sparkles size={12} color="#f9a8d4" />
                    <span style={{ fontSize: '0.76rem', color: '#fbcfe8', fontWeight: 700 }}>{t('face_result.detail.slides.overview')}</span>
                </div>

                <h2 style={heroTitleStyle}>
                    {data?.title || t('face_result.detail.persona.attraction_type')}
                </h2>

                <p style={heroCopyStyle}>
                    {data?.killer_copy || oneLineSummary}
                </p>

                <p style={heroBodyStyle}>
                    {oneLineSummary}
                </p>

                {hashtags.length > 0 ? (
                    <div style={tagWrapStyle}>
                        {hashtags.map((tag) => (
                            <span key={tag} style={tagStyle}>
                                {tag}
                            </span>
                        ))}
                    </div>
                ) : null}

                {!feedMode && data?.description ? (
                    <div style={{ marginTop: '1rem' }}>
                        <button
                            type="button"
                            onClick={() => setShowDescription((prev) => !prev)}
                            style={ghostButtonStyle}
                        >
                            {showDescription ? t('face_result.detail.persona.hide_type_reason') : t('face_result.detail.persona.show_type_reason')}
                        </button>
                        {showDescription ? (
                            <p style={detailTextStyle}>
                                {data.description}
                            </p>
                        ) : null}
                    </div>
                ) : null}
            </div>

            <ScoreRadarChart
                baseScores={data?.scores?.base}
                spiceScores={data?.scores?.spice}
                compact={feedMode}
                summaryLine={strengthSummary.length > 0 ? t('face_result.detail.persona.strongest_axes', { axes: strengthSummary.join(' / ') }) : ''}
            />

            {feedMode ? null : (
                <>
                    <div style={sectionCardStyle}>
                        <div style={eyebrowStyle}>{t('face_result.detail.impression.eyebrow')}</div>
                        <div style={{ display: 'grid', gap: '0.75rem' }}>
                            {firstImpressionItems.map((item, index) => (
                                <div key={item.title} style={miniCardStyle}>
                                    <div style={miniCardTitleStyle}>
                                        {index + 1}. {item.title}
                                    </div>
                                    <div style={miniCardBodyStyle}>{item.body}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.75rem' }}>
                        {bioCards.map((item) => (
                            <div key={item.title} style={miniCardStyle}>
                                <div style={miniCardTitleStyle}>{item.title}</div>
                                <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '0.15rem' }}>{item.subtitle}</div>
                                <div style={{ color: item.color, fontSize: '1.35rem', fontWeight: 800, marginTop: '0.55rem' }}>
                                    {item.value}
                                </div>
                                <div style={{ ...miniCardBodyStyle, marginTop: '0.45rem' }}>{item.summary}</div>
                            </div>
                        ))}
                    </div>

                    <div style={approachCardStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', marginBottom: '0.45rem' }}>
                            <Target size={15} color="#93c5fd" />
                            <div style={{ color: '#bfdbfe', fontWeight: 700, fontSize: '0.9rem' }}>{t('face_result.detail.persona.approachability')}</div>
                        </div>
                        <div style={{ color: '#e2e8f0', fontSize: '0.88rem', lineHeight: 1.65 }}>
                            {t('face_result.detail.persona.approachability_body')}
                        </div>
                        <div style={{ color: '#93c5fd', fontSize: '0.82rem', marginTop: '0.55rem' }}>
                            {t('face_result.detail.persona.first_sensation')}
                        </div>
                    </div>

                    <ExpandableBlock title={t('face_result.detail.persona.ai_summary')}>
                        <div style={{ display: 'grid', gap: '0.75rem' }}>
                            <div style={miniCardStyle}>
                                <div style={miniCardTitleStyle}>{t('face_result.detail.persona.first_impression')}</div>
                                <div style={miniCardBodyStyle}>
                                    {pickSummarySentence(data?.analysis_report?.base_comment) || t('face_result.detail.persona.first_impression_fallback')}
                                </div>
                            </div>
                            <div style={miniCardStyle}>
                                <div style={miniCardTitleStyle}>{t('face_result.detail.persona.attraction_point')}</div>
                                <div style={miniCardBodyStyle}>
                                    {pickSummarySentence(data?.analysis_report?.spice_comment) || t('face_result.detail.persona.attraction_point_fallback')}
                                </div>
                            </div>
                            <div style={miniCardStyle}>
                                <div style={miniCardTitleStyle}>{t('face_result.detail.persona.how_to_use')}</div>
                                <div style={miniCardBodyStyle}>
                                    {pickSummarySentence(data?.analysis_report?.action_plan?.fashion, data?.analysis_report?.action_plan?.hair) || t('face_result.detail.persona.how_to_use_fallback')}
                                </div>
                            </div>
                        </div>
                    </ExpandableBlock>

                    <ExpandableBlock title={t('face_result.detail.persona.flirting_tip')}>
                        <div style={{ position: 'relative' }}>
                            <p style={{ margin: 0, lineHeight: '1.6', fontSize: '0.88rem', color: '#fce7f3', filter: !isPaid ? 'blur(6px)' : 'none' }}>
                                {data?.flirting_tip || t('face_result.detail.persona.flirting_tip_fallback')}
                            </p>
                            {!isPaid ? (
                                <div style={lockedOverlayStyle}>
                                    Locked
                                </div>
                            ) : null}
                        </div>
                    </ExpandableBlock>
                </>
            )}
        </div>
    );
};

const expandableStyle = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '16px',
    padding: '1rem',
};

const expandableButtonStyle = {
    width: '100%',
    background: 'transparent',
    border: 'none',
    color: '#f8fafc',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 0,
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: 700,
};

const expandableLabelStyle = {
    color: '#60a5fa',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    fontSize: '0.8rem',
};

const radarCardStyle = {
    borderRadius: '20px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    padding: '1rem',
};

const radarHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
};

const eyebrowStyle = {
    fontSize: '0.78rem',
    color: '#94a3b8',
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
};

const summaryCaptionStyle = {
    fontSize: '0.84rem',
    color: '#cbd5e1',
    marginTop: '0.25rem',
};

const primaryMetricCardStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    margin: '0.8rem 0 0.9rem',
    padding: '1rem',
    borderRadius: '18px',
    border: '1px solid rgba(96, 165, 250, 0.42)',
    background: 'linear-gradient(135deg, rgba(96,165,250,0.18), rgba(244,114,182,0.12))',
    boxShadow: '0 16px 30px rgba(96, 165, 250, 0.14)',
};

const compactPrimaryMetricCardStyle = {
    ...primaryMetricCardStyle,
    margin: '0.65rem 0 0.7rem',
    padding: '0.75rem 0.85rem',
    borderRadius: '16px',
};

const primaryMetricCopyStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
};

const primaryMetricBadgeStyle = {
    width: 'fit-content',
    padding: '0.25rem 0.6rem',
    borderRadius: '999px',
    background: '#60a5fa',
    color: '#020617',
    fontSize: '0.7rem',
    fontWeight: 900,
    lineHeight: 1,
};

const primaryMetricTitleStyle = {
    color: '#f8fafc',
    fontSize: '1.45rem',
    lineHeight: 1.05,
};

const primaryMetricScoreStyle = {
    minWidth: '64px',
    color: '#f472b6',
    fontSize: '2.35rem',
    fontWeight: 900,
    lineHeight: 1,
    textAlign: 'right',
    textShadow: '0 0 22px rgba(244, 114, 182, 0.42)',
};

const metricGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '0.65rem',
    marginBottom: '1rem',
};

const compactMetricGridStyle = {
    ...metricGridStyle,
    gap: '0.5rem',
    marginBottom: '0.65rem',
};

const metricItemStyle = {
    position: 'relative',
    minHeight: '82px',
    padding: '0.78rem',
    borderRadius: '14px',
    border: '1px solid rgba(255,255,255,0.07)',
    background: 'rgba(255,255,255,0.035)',
};

const compactMetricItemStyle = {
    ...metricItemStyle,
    minHeight: '66px',
    padding: '0.58rem 0.62rem',
    borderRadius: '12px',
};

const primaryMetricItemStyle = {
    border: '1px solid rgba(96,165,250,0.55)',
    background: 'rgba(96,165,250,0.12)',
};

const metricItemTopStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.45rem',
    marginBottom: '0.5rem',
};

const metricNameStyle = {
    color: '#e2e8f0',
    fontSize: '0.84rem',
    fontWeight: 700,
    lineHeight: 1,
};

const metricValueStyle = {
    color: '#94a3b8',
    fontSize: '0.95rem',
    lineHeight: 1,
};

const primaryMetricValueStyle = {
    color: '#60a5fa',
    fontSize: '1.05rem',
    lineHeight: 1,
};

const metricTrackStyle = {
    width: '100%',
    height: '6px',
    overflow: 'hidden',
    borderRadius: '999px',
    background: 'rgba(148,163,184,0.16)',
};

const metricFillStyle = {
    height: '100%',
    borderRadius: 'inherit',
};

const metricPrimaryLabelStyle = {
    display: 'inline-flex',
    marginTop: '0.55rem',
    padding: '0.18rem 0.48rem',
    borderRadius: '999px',
    background: 'rgba(244,114,182,0.18)',
    color: '#f9a8d4',
    fontSize: '0.66rem',
    fontWeight: 900,
    lineHeight: 1,
};

const heroCardStyle = {
    borderRadius: '24px',
    background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 40%, #0f172a 100%)',
    border: '1px solid rgba(139,92,246,0.28)',
    padding: '1.5rem',
    position: 'relative',
    overflow: 'hidden',
};

const heroGlowStyle = {
    position: 'absolute',
    inset: 'auto -30px -40px auto',
    width: '180px',
    height: '180px',
    borderRadius: '50%',
    background: 'rgba(236,72,153,0.12)',
    filter: 'blur(45px)',
};

const heroBadgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    background: 'rgba(255,255,255,0.08)',
    borderRadius: '999px',
    padding: '0.35rem 0.7rem',
    marginBottom: '0.85rem',
};

const heroTitleStyle = {
    margin: 0,
    fontSize: 'clamp(1.7rem, 5vw, 2.4rem)',
    fontWeight: 900,
    color: '#fff',
    lineHeight: 1.05,
};

const heroCopyStyle = {
    margin: '0.65rem 0 0 0',
    color: '#fbcfe8',
    fontSize: '1rem',
    fontWeight: 700,
    lineHeight: 1.5,
};

const heroBodyStyle = {
    margin: '0.85rem 0 0 0',
    color: '#cbd5e1',
    fontSize: '0.93rem',
    lineHeight: 1.65,
    maxWidth: '34rem',
};

const tagWrapStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginTop: '1rem',
};

const tagStyle = {
    fontSize: '0.78rem',
    color: '#c4b5fd',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '999px',
    padding: '0.28rem 0.65rem',
};

const ghostButtonStyle = {
    background: 'transparent',
    border: 'none',
    color: '#93c5fd',
    padding: 0,
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '0.82rem',
};

const detailTextStyle = {
    margin: '0.7rem 0 0 0',
    color: '#cbd5e1',
    fontSize: '0.88rem',
    lineHeight: 1.7,
};

const sectionCardStyle = {
    background: 'rgba(15,23,42,0.7)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '20px',
    padding: '1rem',
};

const miniCardStyle = {
    background: 'rgba(255,255,255,0.03)',
    borderRadius: '14px',
    padding: '0.9rem 1rem',
    border: '1px solid rgba(255,255,255,0.05)',
};

const miniCardTitleStyle = {
    color: '#f8fafc',
    fontWeight: 700,
    fontSize: '0.92rem',
    marginBottom: '0.3rem',
};

const miniCardBodyStyle = {
    color: '#94a3b8',
    fontSize: '0.84rem',
    lineHeight: 1.6,
};

const approachCardStyle = {
    background: 'rgba(59,130,246,0.08)',
    border: '1px solid rgba(59,130,246,0.18)',
    borderRadius: '18px',
    padding: '1rem',
};

const lockedOverlayStyle = {
    position: 'absolute',
    inset: 0,
    display: 'grid',
    placeItems: 'center',
    color: '#fff',
    fontWeight: 700,
};

export default PersonaHero;
